/**
 * Impostazioni email: lista caselle collegate + collegamento nuova casella
 * + modifica di una casella esistente (server IMAP/SMTP, SSL o STARTTLS,
 * password, nome, firma). La password e' write-only (mai mostrata; in
 * modifica lasciarla vuota = invariata). Host/porte avanzate precompilate
 * coi default Aruba e modificabili. Test connessione e scollegamento per
 * casella. Ogni salvataggio ri-verifica la connessione lato backend.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Pencil, Plug, Star, Trash2, XCircle } from 'lucide-react';
import { Badge, Button, Card, Input, Label, Tooltip } from '@floraltech/ui';
import { useAuth } from '@shell/AuthContext';
import { emailFetch, type EmailAccount } from '../lib/api';
import { EtichetteManager } from '../components/EtichetteManager';

const DEFAULT_IMAP_HOST = 'imaps.aruba.it';
const DEFAULT_IMAP_PORT_SSL = 993;
const DEFAULT_IMAP_PORT_STARTTLS = 143;
const DEFAULT_SMTP_HOST = 'smtps.aruba.it';
const DEFAULT_SMTP_PORT_SSL = 465;
const DEFAULT_SMTP_PORT_STARTTLS = 587;

/** Selettore a due stati SSL diretto / STARTTLS (niente opzione in chiaro). */
function SecuritySelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: boolean;
  onChange: (ssl: boolean) => void;
}) {
  return (
    <div id={id} role="radiogroup" className="flex rounded-md border">
      <button
        type="button"
        role="radio"
        aria-checked={value}
        className={`flex-1 rounded-l-md px-2 py-1.5 text-xs ${
          value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
        }`}
        onClick={() => onChange(true)}
      >
        SSL/TLS
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={!value}
        className={`flex-1 rounded-r-md px-2 py-1.5 text-xs ${
          !value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
        }`}
        onClick={() => onChange(false)}
      >
        STARTTLS
      </button>
    </div>
  );
}

export function AccountSettings() {
  const navigate = useNavigate();
  const { hasPermesso, user, reload: reloadAuth } = useAuth();
  const puoGestirePec = hasPermesso('email.pec_gestione');
  const puoGestireEtichette = hasPermesso('email.etichette_gestione');

  // Casella predefinita (preferenza utente su /auth/me): toggle con stella.
  const impostaPredefinita = async (accountId: string) => {
    const nuova = user?.email_account_default === accountId ? null : accountId;
    const token = localStorage.getItem('token') ?? '';
    const res = await fetch('/api/v1/auth/me', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ email_account_default: nuova }),
    });
    if (res.ok) await reloadAuth();
  };
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [mostraForm, setMostraForm] = useState(false);
  const [avanzate, setAvanzate] = useState(false);
  const [salvataggio, setSalvataggio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [esitiTest, setEsitiTest] = useState<Record<string, boolean | null>>({});

  // null = collegamento nuova casella; altrimenti id della casella in modifica.
  const [editId, setEditId] = useState<string | null>(null);

  const [indirizzo, setIndirizzo] = useState('');
  const [password, setPassword] = useState('');
  const [tipo, setTipo] = useState<'personale' | 'pec'>('personale');
  const [nomeVisualizzato, setNomeVisualizzato] = useState('');
  const [firma, setFirma] = useState('');
  const [imapHost, setImapHost] = useState(DEFAULT_IMAP_HOST);
  const [imapPort, setImapPort] = useState(DEFAULT_IMAP_PORT_SSL);
  const [imapSsl, setImapSsl] = useState(true);
  const [smtpHost, setSmtpHost] = useState(DEFAULT_SMTP_HOST);
  const [smtpPort, setSmtpPort] = useState(DEFAULT_SMTP_PORT_SSL);
  const [smtpSsl, setSmtpSsl] = useState(true);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const res = await emailFetch('/account');
      if (res.ok) setAccounts(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carica();
  }, [carica]);

  const resetForm = () => {
    setEditId(null);
    setIndirizzo('');
    setPassword('');
    setTipo('personale');
    setNomeVisualizzato('');
    setFirma('');
    setImapHost(DEFAULT_IMAP_HOST);
    setImapPort(DEFAULT_IMAP_PORT_SSL);
    setImapSsl(true);
    setSmtpHost(DEFAULT_SMTP_HOST);
    setSmtpPort(DEFAULT_SMTP_PORT_SSL);
    setSmtpSsl(true);
    setAvanzate(false);
    setErrore(null);
  };

  /** Cambio sicurezza IMAP: adegua la porta solo se era quella standard dell'altra modalita'. */
  const cambiaImapSsl = (ssl: boolean) => {
    setImapSsl(ssl);
    setImapPort((p) =>
      ssl
        ? p === DEFAULT_IMAP_PORT_STARTTLS
          ? DEFAULT_IMAP_PORT_SSL
          : p
        : p === DEFAULT_IMAP_PORT_SSL
          ? DEFAULT_IMAP_PORT_STARTTLS
          : p
    );
  };

  const cambiaSmtpSsl = (ssl: boolean) => {
    setSmtpSsl(ssl);
    setSmtpPort((p) =>
      ssl
        ? p === DEFAULT_SMTP_PORT_STARTTLS
          ? DEFAULT_SMTP_PORT_SSL
          : p
        : p === DEFAULT_SMTP_PORT_SSL
          ? DEFAULT_SMTP_PORT_STARTTLS
          : p
    );
  };

  /** Apre il form precompilato per modificare una casella esistente. */
  const modifica = (a: EmailAccount) => {
    setEditId(a.id);
    setIndirizzo(a.indirizzo);
    setPassword('');
    // tipo non impostato: in edit il selettore e' nascosto (!editId) e la
    // PATCH non lo invia; ogni uscita dall'edit passa da resetForm().
    setNomeVisualizzato(a.nome_visualizzato ?? '');
    setFirma(a.firma ?? '');
    setImapHost(a.imap_host ?? DEFAULT_IMAP_HOST);
    setImapPort(a.imap_port ?? (a.imap_ssl ? DEFAULT_IMAP_PORT_SSL : DEFAULT_IMAP_PORT_STARTTLS));
    setImapSsl(a.imap_ssl);
    setSmtpHost(a.smtp_host ?? DEFAULT_SMTP_HOST);
    setSmtpPort(a.smtp_port ?? (a.smtp_ssl ? DEFAULT_SMTP_PORT_SSL : DEFAULT_SMTP_PORT_STARTTLS));
    setSmtpSsl(a.smtp_ssl);
    setAvanzate(true);
    setErrore(null);
    setMostraForm(true);
  };

  const salva = async () => {
    setSalvataggio(true);
    setErrore(null);
    try {
      const server = {
        imap_host: imapHost || null,
        imap_port: imapPort || null,
        imap_ssl: imapSsl,
        smtp_host: smtpHost || null,
        smtp_port: smtpPort || null,
        smtp_ssl: smtpSsl,
      };
      const res = editId
        ? await emailFetch(`/account/${editId}`, {
            method: 'PATCH',
            body: JSON.stringify({
              ...server,
              nome_visualizzato: nomeVisualizzato || null,
              firma: firma || null,
              ...(password ? { password } : {}),
            }),
          })
        : await emailFetch('/account', {
            method: 'POST',
            body: JSON.stringify({
              ...server,
              indirizzo,
              password,
              tipo,
              nome_visualizzato: nomeVisualizzato || null,
              firma: firma || null,
            }),
          });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setErrore(body?.detail ?? `Errore ${res.status}`);
        return;
      }
      resetForm();
      setMostraForm(false);
      await carica();
    } catch {
      setErrore('Errore di rete');
    } finally {
      setSalvataggio(false);
    }
  };

  const testa = async (id: string) => {
    setEsitiTest((s) => ({ ...s, [id]: null }));
    const res = await emailFetch(`/account/${id}/test`, { method: 'POST' });
    const body = res.ok ? await res.json() : { ok: false };
    setEsitiTest((s) => ({ ...s, [id]: Boolean(body.ok) }));
    await carica();
  };

  const scollega = async (id: string, indirizzoAccount: string) => {
    if (
      !window.confirm(
        `Scollegare la casella ${indirizzoAccount}? I messaggi sincronizzati verranno rimossi dall'ERP (la casella reale non viene toccata).`
      )
    ) {
      return;
    }
    await emailFetch(`/account/${id}`, { method: 'DELETE' });
    await carica();
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} aria-label="Indietro">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-semibold">Impostazioni email</h1>
      </div>

      <Card className="p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-medium">Caselle collegate</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/email/regole')}>
              Regole
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (mostraForm) {
                  resetForm();
                  setMostraForm(false);
                } else {
                  setMostraForm(true);
                }
              }}
            >
              {mostraForm ? 'Chiudi' : 'Collega casella'}
            </Button>
          </div>
        </div>

        {loading && <p className="mt-3 text-sm text-muted-foreground">Caricamento...</p>}
        {!loading && accounts.length === 0 && (
          <p className="mt-3 text-sm text-muted-foreground">Nessuna casella collegata.</p>
        )}

        <ul className="mt-3 divide-y">
          {accounts.map((a) => {
            const azioniVisibili = a.tipo !== 'pec' || puoGestirePec;
            return (
              <li key={a.id} className="flex items-center gap-3 py-3">
                <div className="flex-1">
                  <div className="flex items-center text-sm font-medium">
                    {a.indirizzo}
                    {a.tipo === 'pec' && <Badge variant="secondary" className="ml-2">PEC</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {a.nome_visualizzato || '-'}
                    {a.ultimo_sync_at &&
                      ` - ultimo sync ${new Date(a.ultimo_sync_at).toLocaleString('it-IT')}`}
                  </p>
                  {a.stato === 'errore_auth' && (
                    <p className="text-xs text-destructive">
                      Autenticazione fallita{a.ultimo_errore ? `: ${a.ultimo_errore}` : ''}
                    </p>
                  )}
                  {a.stato === 'errore_temporaneo' && (
                    <p className="text-xs text-warning">
                      Server non raggiungibile, il sistema ritenta da solo
                      {a.ultimo_errore ? `: ${a.ultimo_errore}` : ''}
                    </p>
                  )}
                </div>
                {a.stato === 'errore_auth' ? (
                  <Badge variant="destructive">errore</Badge>
                ) : a.stato === 'errore_temporaneo' ? (
                  <Badge variant="outline" className="border-warning text-warning">
                    ritento
                  </Badge>
                ) : (
                  <Badge variant="outline">{a.stato}</Badge>
                )}
                {esitiTest[a.id] === true && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                {esitiTest[a.id] === false && <XCircle className="h-4 w-4 text-destructive" />}
                <Tooltip
                  label={
                    user?.email_account_default === a.id
                      ? 'Casella predefinita (clic per rimuovere)'
                      : 'Imposta come casella predefinita'
                  }
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => impostaPredefinita(a.id)}
                    aria-label={`Casella predefinita ${a.indirizzo}`}
                    aria-pressed={user?.email_account_default === a.id}
                  >
                    <Star
                      className={`h-4 w-4 ${
                        user?.email_account_default === a.id
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-muted-foreground'
                      }`}
                    />
                  </Button>
                </Tooltip>
                {azioniVisibili && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => testa(a.id)}>
                      <Plug className="mr-1 h-4 w-4" />
                      Test
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => modifica(a)}
                      aria-label={`Modifica ${a.indirizzo}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => scollega(a.id, a.indirizzo)}
                      aria-label={`Scollega ${a.indirizzo}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </Card>

      {puoGestireEtichette && <EtichetteManager />}

      {mostraForm && (
        <Card className="space-y-3 p-4">
          <h2 className="font-medium">
            {editId ? `Modifica casella ${indirizzo}` : 'Collega una casella'}
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {!editId && (
              <div>
                <Label htmlFor="acc-indirizzo">Indirizzo email</Label>
                <Input
                  id="acc-indirizzo"
                  type="email"
                  value={indirizzo}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setIndirizzo(e.target.value)
                  }
                  placeholder="nome@floraltech.it"
                />
              </div>
            )}
            {!editId && puoGestirePec && (
              <div>
                <Label htmlFor="acc-tipo">Tipo casella</Label>
                <div id="acc-tipo" role="radiogroup" className="flex rounded-md border">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={tipo === 'personale'}
                    className={`flex-1 rounded-l-md px-2 py-1.5 text-xs ${
                      tipo === 'personale'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground'
                    }`}
                    onClick={() => setTipo('personale')}
                  >
                    Personale
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={tipo === 'pec'}
                    className={`flex-1 rounded-r-md px-2 py-1.5 text-xs ${
                      tipo === 'pec'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground'
                    }`}
                    onClick={() => setTipo('pec')}
                  >
                    PEC aziendale
                  </button>
                </div>
                {tipo === 'pec' && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Casella condivisa a livello aziendale. L'accesso e' regolato dal gruppo PEC
                    Aziendale.
                  </p>
                )}
              </div>
            )}
            <div>
              <Label htmlFor="acc-password">
                {editId ? 'Password (vuota = invariata)' : 'Password casella'}
              </Label>
              <Input
                id="acc-password"
                type="password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPassword(e.target.value)
                }
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label htmlFor="acc-nome">Nome visualizzato</Label>
              <Input
                id="acc-nome"
                value={nomeVisualizzato}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNomeVisualizzato(e.target.value)
                }
              />
            </div>
            <div>
              <Label htmlFor="acc-firma">Firma</Label>
              <Input
                id="acc-firma"
                value={firma}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirma(e.target.value)}
              />
            </div>
          </div>

          <button
            type="button"
            className="text-xs text-muted-foreground underline"
            onClick={() => setAvanzate((v) => !v)}
          >
            {avanzate ? 'Nascondi impostazioni server' : 'Impostazioni server (avanzate)'}
          </button>
          {avanzate && (
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-2">
                <Label htmlFor="acc-imap-host">Host IMAP</Label>
                <Input
                  id="acc-imap-host"
                  value={imapHost}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setImapHost(e.target.value)
                  }
                />
              </div>
              <div>
                <Label htmlFor="acc-imap-port">Porta IMAP</Label>
                <Input
                  id="acc-imap-port"
                  type="number"
                  value={imapPort}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setImapPort(Number(e.target.value))
                  }
                />
              </div>
              <div>
                <Label htmlFor="acc-imap-sec">Sicurezza IMAP</Label>
                <SecuritySelect id="acc-imap-sec" value={imapSsl} onChange={cambiaImapSsl} />
              </div>
              <div className="col-span-2">
                <Label htmlFor="acc-smtp-host">Host SMTP</Label>
                <Input
                  id="acc-smtp-host"
                  value={smtpHost}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSmtpHost(e.target.value)
                  }
                />
              </div>
              <div>
                <Label htmlFor="acc-smtp-port">Porta SMTP</Label>
                <Input
                  id="acc-smtp-port"
                  type="number"
                  value={smtpPort}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSmtpPort(Number(e.target.value))
                  }
                />
              </div>
              <div>
                <Label htmlFor="acc-smtp-sec">Sicurezza SMTP</Label>
                <SecuritySelect id="acc-smtp-sec" value={smtpSsl} onChange={cambiaSmtpSsl} />
              </div>
            </div>
          )}

          {errore && <p className="text-sm text-destructive">{errore}</p>}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setMostraForm(false);
              }}
              disabled={salvataggio}
            >
              Annulla
            </Button>
            <Button
              onClick={salva}
              disabled={salvataggio || (!editId && (!indirizzo || !password))}
            >
              {salvataggio
                ? 'Verifica connessione...'
                : editId
                  ? 'Salva modifiche'
                  : 'Collega'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
